<template>
  <DynamicActionsMenu
    :actions="actions"
    :options="menuOptions"
    @action="handleAction"
  >
    <template #trigger>
      <slot name="trigger">
        <EllipsisIcon class="tooltip-trigger" width="20" />
      </slot>
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </DynamicActionsMenu>
</template>

<script setup lang="ts">
import { computed } from "vue";

import DynamicActionsMenu from "@/components/DynamicActionsMenu.vue";
import EllipsisIcon from "@/images/dots-horizontal-outline.svg?component";
import type { Invoice } from "@/typings/invoice";
import type { InvoiceMenuAction, MenuAction, TippyOptions } from "@/typings/ui";
import { InvoiceAction } from "@/typings/ui";

const props = defineProps<{
  invoice: Invoice;
  tippyOptions?: TippyOptions;
  visibleActions?: InvoiceAction[];
  // True when the tenant has Progress Billing turned on in settings.
  // Lifted from the parent ContextMenu (which fetches it lazily) so the
  // "Configure Payment Schedule" entry only appears when the tenant
  // actually permits SPB.
  tenantSpbEnabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "invoice-action", payload: { id: number; action: InvoiceAction }): void;
}>();

const menuOptions = computed<TippyOptions>(() => ({
  theme: "light",
  appendTo: document.body,
  ...props.tippyOptions
}));

const fullyPaid = computed<boolean>(() => {
  const inv = props.invoice;
  const spb = inv.progress_billing;
  if (spb?.is_progress_billing) {
    return (spb.total_balance_remaining_cents ?? 0) <= 0;
  }
  return (inv.financials?.balance_cents ?? 0) <= 0;
});

const spbActive = computed<boolean>(
  () => props.invoice?.progress_billing?.is_progress_billing === true
);
const spbCancelled = computed<boolean>(() =>
  Boolean((props.invoice?.progress_billing as any)?.cancelled)
);
const spbLocked = computed<boolean>(() => Boolean(props.invoice?.finalized));

const allActions = computed<InvoiceMenuAction[]>(() => {
  const inv = props.invoice;
  const links = inv.links || ({} as NonNullable<Invoice["links"]>);

  // Groups order the menu visually. Adjacent items with different groups
  // get a 1px divider; ActionsMenu doesn't print section labels because
  // the headers read as noise when only some groups have them. Order is
  // workflow-driven: status changes first, then payment ops (incl. SPB
  // schedule edits next to refund/resubmit), then content edits, then
  // delivery/accounting.
  const base: InvoiceMenuAction[] = [
    {
      key: InvoiceAction.Approve,
      label: "Approve Invoice",
      icon: "fa-regular fa-thumbs-up",
      eventName: "approve-invoice",
      className: "menu-item",
      link: links.invoice_approve,
      group: "status"
    },
    {
      key: InvoiceAction.Unapprove,
      label: "Edit Invoice",
      icon: "far fa-pen-to-square",
      eventName: "unapprove-invoice",
      className: "menu-item",
      link: links.invoice_break,
      group: "status"
    },
    {
      key: InvoiceAction.Void,
      label: "Void Invoice",
      icon: "fa-regular fa-ban",
      eventName: "void-invoice",
      className: "menu-item",
      link: links.void,
      group: "status"
    },
    {
      key: InvoiceAction.AcceptPayment,
      label: "Accept Payment",
      icon: "fa-regular fa-credit-card",
      eventName: "accept-payment",
      className: "menu-item",
      link: links.payment_create,
      disabled: !links.payment_create || fullyPaid.value,
      group: "payment"
    },
    {
      key: InvoiceAction.IssueRefund,
      label: "Issue Refund",
      icon: "fa-regular fa-money-check-edit-alt",
      eventName: "issue-refund",
      className: "menu-item",
      link: links.invoice_refund,
      group: "payment"
    },
    // SPB plan management. Single entry whose label/visibility depends
    // on the invoice's current SPB state:
    //   - SPB on, unlocked     → "Edit Payment Schedule"
    //   - SPB on, locked/cancel → "View Payment Schedule"
    //   - SPB off, tenant on    → "Add Payment Schedule"
    //   - SPB off, tenant off / no job → hidden
    // Handler in InvoiceActionsContextMenu opens the same modal in all
    // cases; the modal renders the create vs. edit form based on the
    // fetched plan. The entry carries no link, so an explicit `disabled`
    // is required to override DynamicActionsMenu's link-required default;
    // it's disabled only when adding a schedule to a fully-paid invoice
    // (the backend rejects a $0 target anyway). Edit/View stay enabled.
    {
      key: InvoiceAction.EditPaymentSchedule,
      label: spbActive.value
        ? spbLocked.value || spbCancelled.value
          ? "View Payment Schedule"
          : "Edit Payment Schedule"
        : "Add Payment Schedule",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-payment-schedule",
      className: "menu-item",
      visible:
        spbActive.value ||
        Boolean(props.tenantSpbEnabled && props.invoice?.job_id),
      disabled: !spbActive.value && fullyPaid.value,
      group: "payment"
    },
    {
      key: InvoiceAction.RemovePaymentSchedule,
      label: "Remove Payment Schedule",
      icon: "fa-regular fa-calendar-xmark",
      eventName: "remove-payment-schedule",
      className: "menu-item",
      visible: spbActive.value && !spbLocked.value && !spbCancelled.value,
      disabled: false,
      group: "payment"
    },
    {
      key: InvoiceAction.Resubmit,
      label: "Resubmit for Payment",
      icon: "fa-regular fa-credit-card",
      eventName: "resubmit-invoice",
      className: "menu-item",
      link: links.resubmit,
      group: "payment"
    },
    {
      key: InvoiceAction.EditDetails,
      label: "Edit Details",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-details",
      className: "menu-item",
      link: links.update_details,
      group: "edit"
    },
    {
      key: InvoiceAction.EditDate,
      label: "Edit Date",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-date",
      className: "menu-item",
      link: links.edit_invoice_date,
      tooltip: !links.edit_invoice_date
        ? "Invoice must be approved before date can be edited."
        : "",
      group: "edit"
    },
    ...(inv.exclude_from_batching
      ? [
          {
            key: InvoiceAction.ResumeBatch,
            label: "Resume Batching",
            icon: "fa-regular fa-badge-check",
            eventName: "resume-batch",
            className: "menu-item",
            link: links.update_batching,
            group: "share"
          } as InvoiceMenuAction
        ]
      : [
          {
            key: InvoiceAction.DoNotBatch,
            label: "Do Not Batch",
            icon: "fa-regular fa-file-circle-xmark",
            eventName: "do-not-batch",
            className: "menu-item",
            link: links.update_batching,
            group: "share"
          } as InvoiceMenuAction
        ]),
    {
      key: InvoiceAction.Download,
      label: "Download Invoice",
      icon: "fa-regular fa-download",
      eventName: "download",
      className: "menu-item",
      link: links.invoice_pdf,
      group: "share"
    },
    {
      key: InvoiceAction.Email,
      label: "Email",
      icon: "fa-regular fa-envelope",
      eventName: "email-invoice",
      className: "menu-item",
      link: links.invoice_email,
      group: "share"
    }
  ];

  return base;
});

const actions = computed<InvoiceMenuAction[]>(() => {
  if (!props.visibleActions) return allActions.value;
  return allActions.value.filter((a) => props.visibleActions!.includes(a.key));
});

function handleAction(action: MenuAction) {
  const invoiceAction = action as InvoiceMenuAction;
  emit("invoice-action", {
    id: props.invoice.invoice_id,
    action: invoiceAction.key
  });
}
</script>

<style lang="scss" scoped>
.tooltip-trigger {
  text-align: center;
  cursor: pointer;
}
</style>
